import { AssetUIModel, TableColumn } from '@thehubz/client/core';
import { UsageRights } from '@thehubz/client/data-access-api';
import { AnyType, MediaCategories, MULTI_VALUE, TerritoryCategories, TerritoryCategoriesLufthansaGroup } from '@thehubz/shared/model';

export function availableBuyoutTitles(asset: AssetUIModel) {
  return asset?.usageRights ? asset.usageRights.map((right) => right.buyoutRightsTitle) : [];
}

export function updateAvailableBuyoutTitles(availableTitles: string[], usageRights: UsageRights[], index: number) {
  const selectedBuyoutTitles = usageRights[index].buyoutRightsTitle;
  const titleIndex = availableTitles.indexOf(selectedBuyoutTitles);
  if (titleIndex > -1) {
    availableTitles.splice(titleIndex, 1);
  }
}

export function getSelectedCategories(values: string[], col: TableColumn): { _id: string; name: string }[] {
  const source = col.columnDef === 'territory' ? [...TerritoryCategories, ...TerritoryCategoriesLufthansaGroup] : MediaCategories;
  return source.filter((category) => values.includes(category._id));
}

export function getGroupedOptions(col: TableColumn): Record<string, string[]> {
  return (col.options && groupByCategory(col.options)) || {};
}

export function groupByCategory(list: AnyType[]) {
  const categoryMap: AnyType = {};
  list.forEach(({ _id, categories }) => {
    if (categories && categories.length > 0) {
      categories.forEach((category: string) => {
        if (!categoryMap[category]) {
          categoryMap[category] = [];
        }
        categoryMap[category].push(_id);
      });
    }
  });

  return categoryMap;
}

export function getUnmatchedValues(arr1: string[], arr2: string[]) {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  const unmatchedFromArr1 = arr1.filter((item) => !set2.has(item));
  const unmatchedFromArr2 = arr2.filter((item) => !set1.has(item));

  return [...new Set([...unmatchedFromArr1, ...unmatchedFromArr2])]; // remove any duplicates
}

export function removeDuplicates(arr: string[]) {
  return [...new Set(arr)];
}

export function singleSelectedCountries(
  values: string[],
  list: { _id: string; name: string; categories: string[] }[],
  filterSelectedCategory: { _id: string; name: string }[],
) {
  if (values.includes(MULTI_VALUE)) return values;

  const selectedCategoryIds = filterSelectedCategory.map((c) => c._id);
  return list
    .filter((tList) => values.includes(tList._id))
    .filter((country) => !country.categories.some((cat) => selectedCategoryIds.includes(cat)))
    .map((country) => country._id);
}

/**
 * Utility method to update usage/makeBuyout rights.
 */
export function updateRights(updatedRights: AnyType[] | undefined, existingRights: AnyType[] | undefined, setRights: (newRights: AnyType[]) => void): void {
  if (!updatedRights || updatedRights.length === 0) return;

  const hasNewEntries = updatedRights.some((updatedRight) => {
    const existingRight = existingRights?.find((right) => right.buyoutRightsTitle === updatedRight.buyoutRightsTitle);

    return !existingRight || updatedRight.buyoutRights.length > (existingRight.buyoutRights?.length || 0);
  });

  if (!hasNewEntries) return;

  if (existingRights) {
    updatedRights.forEach((updatedRight) => {
      const existingRight = existingRights.find((right) => right.buyoutRightsTitle === updatedRight.buyoutRightsTitle);

      if (existingRight) {
        if (updatedRight.buyoutRights.length > existingRight.buyoutRights.length) {
          const newRights = updatedRight.buyoutRights.slice(existingRight.buyoutRights.length);
          existingRight.buyoutRights.push(...newRights);
        }
      } else {
        existingRights.push(updatedRight);
      }
    });
  } else {
    setRights(updatedRights);
  }
}
